/**
 * MCP-client OAuth consent page (TARA_PLUGIN_OAUTH2_PLAN.md WS3).
 *
 * Lives at `/oauth/authorize`. The gateway (the OAuth issuer the Claude
 * plugin / MCP connectors talk to) 302s the browser here with the standard
 * authorization-request parameters. This page:
 *
 *   1. validates the request shape (client_id, redirect_uri, PKCE challenge);
 *   2. if the visitor is signed out, parks the request and sends them to
 *      `/login` — App.tsx brings them straight back once the identity resolves;
 *   3. shows the standard consent layout: WHO is asking (the registered client
 *      name), WHO is granting (the signed-in employee, with a "Not you?" link),
 *      WHAT the grant allows in plain language, and the brand count with the
 *      full list behind a disclosure;
 *   4. Approve / Deny → central-api answers with the redirect URL (carrying a
 *      single-use code, or `error=access_denied`) and the browser goes there.
 *
 * Rules that are load-bearing (plan D16): this page uses ONLY the agency
 * identity API (never a seller-JWT-only call — the `/integrations` 401-logout
 * incident) and gates on `employeeIdentity`, never on `sellerToken`. A
 * malformed or expired request gets an error card, never a redirect.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import '../../styles/pages/oauth-consent/oauthConsent.scss';

import MainSquareButton from '../../ui/buttons/MainSquareButton';
import { RootState } from '../../store/store';
import {
  authorizeMcpClient,
  fetchMcpOAuthClient,
  McpOAuthAuthorizeParams,
} from '../../lib/api/identity/identity-api';
import { stashPendingMcpOAuth } from '../../lib/mcp-oauth/pending';
import { tokenRevokeAtLogOutUser } from '../../lib/api/logout-user';

const REQUIRED_PARAMS = ['client_id', 'redirect_uri', 'code_challenge'] as const;
const PASSTHROUGH_PARAMS = [
  'client_id',
  'redirect_uri',
  'state',
  'code_challenge',
  'code_challenge_method',
  'resource',
  'scope',
] as const;

type Phase =
  | 'resolving' // session restore / identity fetch in flight
  | 'login' // redirecting to /login
  | 'invalid' // request malformed or client unknown
  | 'loading' // fetching the client name
  | 'ready'
  | 'submitting'
  | 'redirecting'
  | 'error';

const OauthConsent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const agencyToken = useSelector(
    (state: RootState) => state.auth?.agency?.accessToken
  );
  const employeeIdentity = useSelector(
    (state: RootState) => state.auth?.agency?.employeeIdentity
  );
  const sessionResolving = useSelector(
    (state: RootState) => state.auth?.agency?.sessionResolving ?? false
  );

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const missing = useMemo(
    () => REQUIRED_PARAMS.filter((key) => !params.get(key)),
    [params]
  );

  const [phase, setPhase] = useState<Phase>('resolving');
  const [clientName, setClientName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showBrands, setShowBrands] = useState(false);

  const hasToken = !!agencyToken && agencyToken.length > 0;

  // ----- resolve the phase from the request + the session -----
  useEffect(() => {
    if (missing.length > 0) {
      setPhase('invalid');
      return;
    }
    if (sessionResolving) {
      setPhase('resolving');
      return;
    }
    if (!hasToken) {
      // Park the request and go sign in; App.tsx brings us back.
      stashPendingMcpOAuth(location.search);
      setPhase('login');
      navigate('/login', { replace: true });
      return;
    }
    if (!employeeIdentity) {
      // The token is present; App.tsx's identity bootstrap is fetching /me.
      setPhase('resolving');
      return;
    }

    let cancelled = false;
    setPhase('loading');
    fetchMcpOAuthClient(params.get('client_id') as string)
      .then((client) => {
        if (cancelled) return;
        setClientName(client.client_name || t('OauthConsent_UnknownClient'));
        setPhase('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setPhase('invalid');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missing.length, sessionResolving, hasToken, employeeIdentity, params]);

  const brands = useMemo(() => {
    const fromBrands = (employeeIdentity?.accessible_brands ?? [])
      .map((b: any) => b?.brand_name || b?.brand_slug)
      .filter(Boolean);
    if (fromBrands.length > 0) return fromBrands as string[];
    return (employeeIdentity?.accessible_shops ?? [])
      .map((s: any) => s?.name || s?.slug)
      .filter(Boolean) as string[];
  }, [employeeIdentity]);

  const submit = async (decision: 'approve' | 'deny') => {
    if (phase !== 'ready' && phase !== 'error') return;
    setPhase('submitting');
    setErrorMessage('');

    const payload: McpOAuthAuthorizeParams = {
      client_id: params.get('client_id') as string,
      redirect_uri: params.get('redirect_uri') as string,
      code_challenge: params.get('code_challenge') as string,
      decision,
    };
    for (const key of PASSTHROUGH_PARAMS) {
      const value = params.get(key);
      if (value && !(key in payload)) {
        (payload as any)[key] = value;
      }
    }

    try {
      const { redirect_to } = await authorizeMcpClient(payload);
      if (!redirect_to || typeof redirect_to !== 'string') {
        throw new Error(t('OauthConsent_NoRedirect'));
      }
      setPhase('redirecting');
      window.location.assign(redirect_to);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.warn('[OauthConsent] authorize failed', err);
      setErrorMessage(err?.message || t('OauthConsent_FailedFallback'));
      setPhase('error');
    }
  };

  const handleNotYou = async () => {
    // Keep the request parked across the sign-out reload; App.tsx returns
    // the next signer here.
    stashPendingMcpOAuth(location.search);
    await tokenRevokeAtLogOutUser();
  };

  // ----- render -----
  const card = (children: JSX.Element) => (
    <div className='oauthConsent'>
      <div className='oauthConsent__card' role='dialog' aria-labelledby='oauthConsent-title'>
        <div className='oauthConsent__logo' aria-hidden='true'>
          AD<span>UP</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (phase === 'invalid') {
    return card(
      <>
        <h1 id='oauthConsent-title' className='oauthConsent__title'>
          {t('OauthConsent_InvalidTitle')}
        </h1>
        <p className='oauthConsent__body'>{t('OauthConsent_InvalidBody')}</p>
      </>
    );
  }

  if (phase === 'resolving' || phase === 'login' || phase === 'loading') {
    return card(
      <>
        <h1 id='oauthConsent-title' className='oauthConsent__title'>
          {t('OauthConsent_Title')}
        </h1>
        <p className='oauthConsent__body oauthConsent__body--muted'>
          {phase === 'login'
            ? t('OauthConsent_LoginRequired')
            : t('OauthConsent_Resolving')}
        </p>
      </>
    );
  }

  if (phase === 'redirecting') {
    return card(
      <>
        <h1 id='oauthConsent-title' className='oauthConsent__title'>
          {t('OauthConsent_Title')}
        </h1>
        <p className='oauthConsent__body oauthConsent__body--muted'>
          {t('OauthConsent_Redirecting')}
        </p>
      </>
    );
  }

  const busy = phase === 'submitting';

  return card(
    <>
      <h1 id='oauthConsent-title' className='oauthConsent__title'>
        {t('OauthConsent_Wants', { client: clientName })}
      </h1>
      <p className='oauthConsent__body'>{t('OauthConsent_Lede')}</p>

      <section className='oauthConsent__section'>
        <h2 className='oauthConsent__label'>{t('OauthConsent_AsAccount')}</h2>
        <div className='oauthConsent__account'>
          <div className='oauthConsent__accountText'>
            <strong>{employeeIdentity?.name || employeeIdentity?.email}</strong>
            {employeeIdentity?.email && employeeIdentity?.name && (
              <span>{employeeIdentity.email}</span>
            )}
            {employeeIdentity?.role && (
              <span className='oauthConsent__role'>{employeeIdentity.role}</span>
            )}
          </div>
          <button
            type='button'
            className='oauthConsent__link'
            onClick={handleNotYou}
            disabled={busy}
          >
            {t('OauthConsent_NotYou')}
          </button>
        </div>
      </section>

      <section className='oauthConsent__section'>
        <h2 className='oauthConsent__label'>{t('OauthConsent_AccessTitle')}</h2>
        <ul className='oauthConsent__access'>
          <li>{t('OauthConsent_AccessRead')}</li>
          <li>{t('OauthConsent_AccessPropose')}</li>
          <li className='oauthConsent__access--never'>
            {t('OauthConsent_AccessNever')}
          </li>
        </ul>
        <div className='oauthConsent__brands'>
          <span>{t('OauthConsent_BrandsCount', { count: brands.length })}</span>
          {brands.length > 0 && (
            <button
              type='button'
              className='oauthConsent__link'
              aria-expanded={showBrands}
              onClick={() => setShowBrands((v) => !v)}
            >
              {showBrands
                ? t('OauthConsent_HideBrands')
                : t('OauthConsent_ShowBrands')}
            </button>
          )}
        </div>
        {showBrands && brands.length > 0 && (
          <ul className='oauthConsent__brandList'>
            {brands.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        )}
      </section>

      {phase === 'error' && (
        <p className='oauthConsent__error' role='alert'>
          {t('OauthConsent_Failed', { message: errorMessage })}
        </p>
      )}

      <div className='oauthConsent__buttons'>
        <MainSquareButton
          type='button'
          value={t('OauthConsent_Deny')}
          buttonType='white'
          normal={true}
          disabled={busy}
          onClick={(e) => {
            e.preventDefault();
            submit('deny');
          }}
        />
        <MainSquareButton
          type='button'
          value={busy ? t('OauthConsent_Approving') : t('OauthConsent_Approve')}
          buttonType='success'
          normal={true}
          loading={busy}
          disabled={busy}
          onClick={(e) => {
            e.preventDefault();
            submit('approve');
          }}
        />
      </div>
      <p className='oauthConsent__footnote'>{t('OauthConsent_Footnote')}</p>
    </>
  );
};

export default OauthConsent;
